import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, FileText, Eye } from 'lucide-react';
import { COMPANY } from '../../../config/company';

/**
 * ClientProposalsTab - Displays list of proposals/estimates linked to the client
 */
const ClientProposalsTab = ({
  language,
  clientEstimates,
  createNewProposal,
  formatDate,
  formatCurrency
}) => {
  // Get status badge with appropriate styling
  const getEstimateStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', labelEs: 'Borrador', color: 'bg-gray-100 text-gray-700' },
      sent: { label: 'Sent', labelEs: 'Enviado', color: 'bg-blue-100 text-blue-700' },
      new_lead: { label: 'New Lead', labelEs: 'Nuevo Lead', color: 'bg-purple-100 text-purple-700' },
      estimate_provided: { label: 'Estimate Provided', labelEs: 'Presupuesto Enviado', color: 'bg-blue-100 text-blue-700' },
      follow_up: { label: 'Follow Up', labelEs: 'Seguimiento', color: 'bg-yellow-100 text-yellow-700' },
      lead_won: { label: 'Won', labelEs: 'Ganado', color: 'bg-green-100 text-green-700' },
      lead_lost: { label: 'Lost', labelEs: 'Perdido', color: 'bg-red-100 text-red-700' },
      approved: { label: 'Approved', labelEs: 'Aprobado', color: 'bg-green-100 text-green-700' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <Badge className={config.color}>
        {language === 'es' ? config.labelEs : config.label}
      </Badge>
    );
  };

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-gray-900">
          {language === 'es' ? 'Propuestas' : 'Proposals'}
        </CardTitle>
        <Button 
          onClick={createNewProposal} 
          size="sm" 
          style={{ backgroundColor: COMPANY.primaryColor }} 
          className="text-gray-900"
        >
          <Plus className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Nueva' : 'New'}
        </Button>
      </CardHeader>
      <CardContent>
        {clientEstimates.length > 0 ? (
          <div className="space-y-3">
            {clientEstimates.map(est => (
              <div 
                key={est.id} 
                className="flex items-center justify-between p-4 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <div>
                  <span className="text-amber-600 font-mono text-sm font-semibold">
                    {est.proposal_id || est.estimate_number}
                  </span>
                  <p className="text-gray-900 capitalize">
                    {est.project_type?.replace(/_/g, ' ')}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {formatDate(est.created_at || est.estimate_date)}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  {getEstimateStatusBadge(est.status || est.lead_status)}
                  <span className="text-green-600 font-semibold">
                    {formatCurrency(est.grand_total)}
                  </span>
                  <Link to={`/leads/${est.id}`}>
                    <Button size="sm" variant="ghost" className="text-gray-600 hover:text-gray-900">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {language === 'es' ? 'Sin propuestas' : 'No proposals yet'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {language === 'es' 
                ? 'Las propuestas se vinculan por correo electrónico' 
                : 'Proposals are linked by email address'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientProposalsTab;
